import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Filter, Loader2, X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function asinFilterLabel(selectedAsins: string[]): string {
  if (selectedAsins.length === 0) return 'All ASINs';
  if (selectedAsins.length === 1) return selectedAsins[0] || '1 ASIN';
  return `${selectedAsins.length} ASINs selected`;
}

function toggleAsin(selectedAsins: string[], asin: string, checked: boolean): string[] {
  if (checked) {
    if (selectedAsins.includes(asin)) return selectedAsins;
    return [...selectedAsins, asin];
  }
  return selectedAsins.filter((item) => item !== asin);
}

export function DaypartingAsinFilter({
  profileId,
  campaignIds,
  keywordIds,
  searchTerms,
  selectedAsins,
  onChange,
}: {
  profileId: string;
  campaignIds: string[];
  keywordIds: string[];
  searchTerms: string[];
  selectedAsins: string[];
  onChange: (asins: string[]) => void;
}) {
  const labelId = useId();
  const searchInputId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draftAsins, setDraftAsins] = useState<string[]>([]);
  const debouncedSearch = useDebouncedValue(search, 250);
  const campaignKey = useMemo(() => [...campaignIds].sort().join(','), [campaignIds]);
  const keywordKey = useMemo(() => [...keywordIds].sort().join(','), [keywordIds]);
  const searchTermKey = useMemo(() => [...searchTerms].sort().join('\n'), [searchTerms]);

  const asinsQuery = useQuery({
    queryKey: [
      'dayparting-v2-asins',
      profileId,
      debouncedSearch,
      campaignKey,
      keywordKey,
      searchTermKey,
    ],
    queryFn: async () => {
      const res = await adsApi.getStreamV2Asins(profileId, {
        q: debouncedSearch || undefined,
        campaignIds: campaignIds.length > 0 ? campaignIds : undefined,
        keywordIds: keywordIds.length > 0 ? keywordIds : undefined,
        searchTerms: searchTerms.length > 0 ? searchTerms : undefined,
        limit: 50,
      });
      if (!res.success) throw new Error('Failed to load ASINs');
      return res.data?.asins ?? [];
    },
    enabled: open && Boolean(profileId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const visibleAsins = useMemo(() => {
    const fromApi = asinsQuery.data ?? [];
    const seen = new Set(fromApi);
    const extras = draftAsins.filter((asin) => !seen.has(asin));
    return [...extras, ...fromApi];
  }, [asinsQuery.data, draftAsins]);

  const activeAsins = open ? draftAsins : selectedAsins;
  const selectedSet = useMemo(() => new Set(activeAsins), [activeAsins]);
  const triggerLabel = asinFilterLabel(selectedAsins);
  const loading = asinsQuery.isPending || asinsQuery.isFetching;
  const isFiltered = selectedAsins.length > 0;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraftAsins(selectedAsins);
      return;
    }
    setDraftAsins(selectedAsins);
    setSearch('');
  };

  const selectVisible = () => {
    const next = new Set(draftAsins);
    const merged = [...draftAsins];
    for (const asin of visibleAsins) {
      if (next.has(asin)) continue;
      next.add(asin);
      merged.push(asin);
    }
    setDraftAsins(merged);
  };

  const applyDraft = () => {
    onChange(draftAsins);
    setOpen(false);
    setSearch('');
  };

  const panel: ReactNode = (
    <>
      <div className="space-y-3 border-b border-border px-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-foreground">Select ASINs</p>
            <p className="text-xs text-foreground-subtle">
              Search advertised products. Check one or more ASINs.
            </p>
          </div>
          {draftAsins.length > 0 ? (
            <button
              type="button"
              className="shrink-0 text-xs font-medium text-foreground-muted hover:text-foreground"
              onClick={() => setDraftAsins([])}
            >
              Clear all
            </button>
          ) : null}
        </div>
        <div>
          <label htmlFor={searchInputId} className="sr-only">
            Search ASINs
          </label>
          <input
            id={searchInputId}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Type to find ASINs"
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            autoFocus
          />
        </div>
        {visibleAsins.length > 0 ? (
          <button
            type="button"
            className="text-xs font-medium text-brand-deep hover:underline"
            onClick={selectVisible}
          >
            Select all matching ({visibleAsins.length})
          </button>
        ) : null}
      </div>
      <div className="max-h-64 overflow-y-auto px-1 py-1" role="listbox" aria-multiselectable="true">
        {asinsQuery.isError ? (
          <p className="px-3 py-4 text-sm text-error">Failed to load ASINs.</p>
        ) : loading && visibleAsins.length === 0 ? (
          <p className="px-3 py-4 text-sm text-foreground-muted">Loading ASINs…</p>
        ) : visibleAsins.length === 0 ? (
          <p className="px-3 py-4 text-sm text-foreground-muted">No ASINs match.</p>
        ) : (
          <ul className="m-0 list-none p-0">
            {visibleAsins.map((asin) => {
              const checked = selectedSet.has(asin);
              return (
                <li key={asin} role="option" aria-selected={checked}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg px-2.5 py-2.5 text-sm',
                      checked ? 'bg-brand-subtle/50' : 'hover:bg-surface-muted'
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-brand/30"
                      checked={checked}
                      onChange={(event) =>
                        setDraftAsins(toggleAsin(draftAsins, asin, event.target.checked))
                      }
                    />
                    <span className="min-w-0 truncate font-medium text-foreground">{asin}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2.5">
        <p className="text-xs text-foreground-subtle">
          {draftAsins.length > 0
            ? `${draftAsins.length} ASIN${draftAsins.length === 1 ? '' : 's'} selected`
            : 'All ASINs included'}
        </p>
        <Button variant="secondary" size="sm" onClick={applyDraft}>
          Done
        </Button>
      </div>
    </>
  );

  return (
    <div
      className="rounded-xl border border-border bg-surface p-3 shadow-sm sm:p-4"
      role="group"
      aria-labelledby={labelId}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <p id={labelId} className="text-sm font-semibold text-foreground">
            ASIN filter
          </p>
          <p className="text-xs text-foreground-subtle">
            Limit the heatmap and summary cards to advertised products. Leave unselected to
            include every ASIN.
          </p>
        </div>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={!profileId}
              aria-labelledby={labelId}
              aria-haspopup="listbox"
              aria-expanded={open}
              className={cn(
                'inline-flex w-full min-w-[14rem] max-w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm lg:w-auto lg:min-w-[18rem]',
                'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                isFiltered
                  ? 'border-brand/40 bg-brand-subtle/40 text-foreground hover:bg-brand-subtle/60'
                  : 'border-border bg-page text-foreground hover:bg-page/80'
              )}
            >
              <span className="inline-flex min-w-0 items-center gap-2">
                <Filter
                  className={cn(
                    'h-4 w-4 shrink-0',
                    isFiltered ? 'text-brand-deep' : 'text-foreground-muted'
                  )}
                  aria-hidden
                />
                <span className="truncate text-left">{triggerLabel}</span>
              </span>
              {loading && open ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-foreground-muted" aria-hidden />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[min(26rem,calc(100vw-2rem))] p-0">
            {panel}
          </PopoverContent>
        </Popover>
      </div>

      {isFiltered ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border/70 pt-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">
            Active
          </span>
          {selectedAsins.map((asin) => (
            <span
              key={asin}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-brand/30 bg-brand-subtle/60 px-2.5 py-1 text-xs text-foreground"
            >
              <span className="truncate">{asin}</span>
              <button
                type="button"
                className="shrink-0 rounded-full text-foreground-muted hover:text-foreground"
                aria-label={`Remove ${asin}`}
                onClick={() => onChange(toggleAsin(selectedAsins, asin, false))}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
          <button
            type="button"
            className="text-xs font-medium text-foreground-muted hover:text-foreground"
            onClick={() => onChange([])}
          >
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  );
}
